
import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Container, Row, Button, Form, FormGroup, Label, Input, Table, Spinner, Badge } from "reactstrap";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import { Btn, H3 } from "../../../AbstractElements";
import { Plus, Trash2, ArrowLeft, Disc, Tool } from "react-feather";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../../../Utils";
import { toast, ToastContainer } from "react-toastify";
import { useForm, Controller, useFieldArray } from "react-hook-form";

const CreateLPO = () => {
    const navigate = useNavigate();
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [lpoType, setLpoType] = useState<'parts' | 'labour' | 'both'>('parts');
    const [sameVendor, setSameVendor] = useState(false);

    // Form setup with nested objects for parts and labour
    const { register, control, handleSubmit, watch, setValue, trigger, formState: { errors } } = useForm({
        defaultValues: {
            common: {
                branch_id: "",
                vehicle_id: "",
                lpo_date: new Date().toISOString().split('T')[0],
                odometer_reading: ""
            },
            parts: {
                vendor_name: "",
                new_vendor_details: "",
                expense_category: "Spare Parts",
                items: [{ item_description: "", part_number: "", quantity: 1, unit_price: 0 }]
            },
            labour: {
                vendor_name: "",
                new_vendor_details: "",
                expense_category: "Maintenance",
                items: [{ item_description: "", quantity: 1, unit_price: 0 }]
            }
        }
    });

    const partsFieldArray = useFieldArray({ control, name: "parts.items" });
    const labourFieldArray = useFieldArray({ control, name: "labour.items" });

    // Watchers
    const selectedBranch = watch("common.branch_id");
    const partsVendor = watch("parts.vendor_name");
    const partsVendorDetails = watch("parts.new_vendor_details");

    // Totals
    const partsItems = watch("parts.items");
    const labourItems = watch("labour.items");

    const partsTotal = partsItems?.reduce((acc, curr) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0) || 0;
    const labourTotal = labourItems?.reduce((acc, curr) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0) || 0;

    useEffect(() => {
        apiPost("branches/list", { status: 'Active' }).then((res: any) => {
            if (Array.isArray(res)) setBranches(res);
            else if (res.data) setBranches(res.data);
        });
    }, []);

    useEffect(() => {
        if (selectedBranch) {
            setVehicles([]);
            setValue("common.vehicle_id", "");
            apiPost("vehicles/list", { branch_id: selectedBranch }).then((res: any) => {
                if (Array.isArray(res)) {
                    setVehicles(res.filter((v: any) => v.status !== 'Sold'));
                } else if (res.data) {
                    setVehicles(res.data.filter((v: any) => v.status !== 'Sold'));
                }
            });
        }
    }, [selectedBranch, setValue]);

    // Handle "Same Vendor" logic
    useEffect(() => {
        if (sameVendor && lpoType === 'both') {
            setValue("labour.vendor_name", partsVendor || "", { shouldValidate: !!partsVendor });
            setValue("labour.new_vendor_details", partsVendorDetails || "", { shouldValidate: !!partsVendor });
        }
    }, [sameVendor, partsVendor, partsVendorDetails, lpoType, setValue]);


    const onSubmit = async (data: any) => {
        // Validation check for empty items arrays based on type
        if ((lpoType === 'parts' || lpoType === 'both') && data.parts.items.length === 0) {
            toast.error("Please add items to the Parts section"); return;
        }
        if ((lpoType === 'labour' || lpoType === 'both') && data.labour.items.length === 0) {
            toast.error("Please add items to the Labour section"); return;
        }

        setSubmitting(true);
        try {
            const lpos = [];

            if (lpoType === 'both') {
                // Hybrid Logic: Create ONE LPO with both parts and labour items using new backend columns
                const mergedItems = [
                    ...data.parts.items.map((i: any) => ({ ...i, part_number: i.part_number || null, item_type: 'Parts' })),
                    ...data.labour.items.map((i: any) => ({ ...i, part_number: null, item_type: 'Labour' }))
                ];

                lpos.push({
                    vendor_name: data.parts.vendor_name,
                    new_vendor_details: data.parts.new_vendor_details,
                    labour_vendor_name: !sameVendor ? data.labour.vendor_name : null,
                    labour_vendor_details: !sameVendor ? data.labour.new_vendor_details : null,
                    expense_category: "Maintenance", // Default category for merged LPO
                    description: `Hybrid LPO: Parts & Labour`,
                    items: mergedItems
                });
            } else {
                // Separate Logic
                if (lpoType === 'parts') {
                    lpos.push({
                        ...data.parts,
                        items: data.parts.items.map((i: any) => ({ ...i, item_type: 'Parts' })),
                        description: 'Parts Requisition'
                    });
                }

                if (lpoType === 'labour') {
                    lpos.push({
                        ...data.labour,
                        items: data.labour.items.map((i: any) => ({ ...i, item_type: 'Labour' })),
                        description: 'Labour/Service Requisition'
                    });
                }
            }

            const payload = {
                common: data.common,
                lpos: lpos
            };

            await apiPost("lpos/store", payload);
            toast.success("LPO Created Successfully");
            setTimeout(() => navigate(`${process.env.PUBLIC_URL}/lpo`), 1000);
        } catch (error: any) {
            const msg = error.response?.data?.message || "Failed to create LPO";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Operations" title="LPO Management" pageTitle="Create LPO" />
            <Container fluid>
                <div className="d-flex justify-content-center mb-4">
                    <div className="btn-group">
                        <Button color={lpoType === 'parts' ? 'primary' : 'outline-primary'} onClick={() => setLpoType('parts')} active={lpoType === 'parts'}>
                            <Disc size={16} className="me-2" />Parts Only
                        </Button>
                        <Button color={lpoType === 'labour' ? 'primary' : 'outline-primary'} onClick={() => setLpoType('labour')} active={lpoType === 'labour'}>
                            <Tool size={16} className="me-2" />Labour Only
                        </Button>
                        <Button color={lpoType === 'both' ? 'primary' : 'outline-primary'} onClick={() => setLpoType('both')} active={lpoType === 'both'}>
                            <span className="me-2">⚡</span>Hybrid (Both)
                        </Button>
                    </div>
                </div>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    {/* COMMON DETAILS */}
                    <Card className="mb-4">
                        <CardBody style={{ backgroundColor: '#f8f9fa' }}>
                            <Row>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            control={control}
                                            name="common.branch_id"
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <Input type="select" {...field} bsSize="sm">
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                                </Input>
                                            )}
                                        />
                                        {errors.common?.branch_id && <span className="text-danger small">{errors.common.branch_id.message as string}</span>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label>Vehicle <span className="text-danger">*</span></Label>
                                        <Controller
                                            control={control}
                                            name="common.vehicle_id"
                                            rules={{ required: "Vehicle is required" }}
                                            render={({ field }) => (
                                                <Input type="select" {...field} disabled={!selectedBranch} bsSize="sm">
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} - {v.model}</option>
                                                    ))}
                                                </Input>
                                            )}
                                        />
                                        {errors.common?.vehicle_id && <span className="text-danger small">{errors.common.vehicle_id.message as string}</span>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label>LPO Date <span className="text-danger">*</span></Label>
                                        <input type="date" className="form-control form-control-sm" {...register("common.lpo_date", { required: "Date is required" })} />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label>Odometer Reading </Label>
                                        <input type="number" className="form-control form-control-sm" placeholder="Current Mileage" {...register("common.odometer_reading")} />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Row>
                        {/* PARTS SECTION */}
                        {(lpoType === 'parts' || lpoType === 'both') && (
                            <Col md={lpoType === 'both' ? 6 : 12}>
                                <Card className="border-top-primary">
                                    <CardBody>
                                        <div className="d-flex justify-content-between mb-3">
                                            <H3 className="text-primary"><Disc className="me-2" size={20} />Parts Details</H3>
                                            <Badge color="light" className="text-dark fs-6">{partsTotal.toFixed(2)}</Badge>
                                        </div>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Vendor (Supplier) <span className="text-danger">*</span></Label>
                                                    <input className="form-control form-control-sm" {...register("parts.vendor_name", { required: (lpoType as string) !== 'labour' })} placeholder="Supplier Name" autoComplete="off" />
                                                    {errors.parts?.vendor_name && <span className="text-danger small">Vendor is required</span>}
                                                </FormGroup>
                                            </Col>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Vendor Details</Label>
                                                    <textarea className="form-control form-control-sm" rows={1} {...register("parts.new_vendor_details")} placeholder="Address / Contact" autoComplete="off" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Category <span className="text-danger">*</span></Label>
                                                    <select className="form-select form-select-sm" {...register("parts.expense_category")}>
                                                        <option value="Spare Parts">Spare Parts</option>
                                                        <option value="Tyres">Tyres</option>
                                                        <option value="Battery">Battery</option>
                                                        <option value="Consumables">Consumables</option>
                                                    </select>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Table bordered size="sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '60%' }}>Part Name</th>
                                                    <th style={{ width: '15%' }}>Qty</th>
                                                    <th style={{ width: '20%' }}>Price</th>
                                                    <th style={{ width: '5%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {partsFieldArray.fields.map((field, index) => {
                                                    const itemError = (errors as any).parts?.items?.[index];
                                                    return (
                                                        <tr key={field.id}>
                                                            <td>
                                                                <input
                                                                    className={`form-control form-control-sm ${itemError?.item_description ? "is-invalid" : "border-0"}`}
                                                                    placeholder="Description"
                                                                    {...register(`parts.items.${index}.item_description` as const, { required: "Part name is required" })} autoComplete="off"
                                                                />
                                                                {itemError?.item_description && <span className="text-danger small">{itemError.item_description.message}</span>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control form-control-sm ${itemError?.quantity ? "is-invalid" : "border-0"}`}
                                                                    {...register(`parts.items.${index}.quantity` as const, { required: "Required", min: { value: 1, message: "Min 1" } })}
                                                                />
                                                                {itemError?.quantity && <span className="text-danger small">{itemError.quantity.message}</span>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control form-control-sm ${itemError?.unit_price ? "is-invalid" : "border-0"}`}
                                                                    step="0.01"
                                                                    {...register(`parts.items.${index}.unit_price` as const, { required: "Required", min: { value: 0.0001, message: "Price should be greater than 0" } })}
                                                                />
                                                                {itemError?.unit_price && <span className="text-danger small">{itemError.unit_price.message}</span>}
                                                            </td>
                                                            <td>
                                                                <Trash2
                                                                    size={14}
                                                                    className={`cursor-pointer ${partsFieldArray.fields.length > 1 ? "text-danger" : "text-muted"}`}
                                                                    onClick={() => partsFieldArray.fields.length > 1 && partsFieldArray.remove(index)}
                                                                    style={{ pointerEvents: partsFieldArray.fields.length > 1 ? 'auto' : 'none' }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        <Button color="primary" outline size="sm" className="w-100" onClick={() => partsFieldArray.append({ item_description: "", quantity: 1, unit_price: 0, part_number: "" })}>
                                            <Plus size={14} /> Add Part
                                        </Button>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}

                        {/* LABOUR SECTION */}
                        {(lpoType === 'labour' || lpoType === 'both') && (
                            <Col md={lpoType === 'both' ? 6 : 12}>
                                <Card className="border-top-warning">
                                    <CardBody>
                                        <div className="d-flex justify-content-between mb-3">
                                            <H3 className="text-warning"><Tool className="me-2" size={20} />Labour Details</H3>
                                            <Badge color="light" className="text-dark fs-6">{labourTotal.toFixed(2)}</Badge>
                                        </div>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <div className="d-flex justify-content-between">
                                                        <Label>Vendor Name (Garage) <span className="text-danger">*</span></Label>
                                                        {lpoType === 'both' && (
                                                            <div className="form-check form-switch">
                                                                <Input
                                                                    type="switch"
                                                                    id="sameVendor"
                                                                    checked={sameVendor}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        setSameVendor(isChecked);
                                                                        if (!isChecked) {
                                                                            setValue("labour.vendor_name", "");
                                                                            setValue("labour.new_vendor_details", "");
                                                                        }
                                                                    }}
                                                                    style={{ transform: 'scale(1.2)', borderColor: sameVendor ? '#198754' : '', backgroundColor: sameVendor ? '#198754' : '' }} autoComplete="off"
                                                                />
                                                                <Label check for="sameVendor" className="ms-2 text-success fw-bold" style={{ fontSize: '1.1em' }}>Same as Parts</Label>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <input className="form-control form-control-sm" {...register("labour.vendor_name", { required: (lpoType as string) !== 'parts' })} placeholder="Garage Name" disabled={sameVendor && lpoType === 'both'} autoComplete="off" />
                                                    {errors.labour?.vendor_name && <span className="text-danger small">Vendor is required</span>}
                                                </FormGroup>
                                            </Col>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Vendor Details</Label>
                                                    <textarea className="form-control form-control-sm" rows={1} {...register("labour.new_vendor_details")} placeholder="Address / Contact" disabled={sameVendor && lpoType === 'both'} autoComplete="off" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Category <span className="text-danger">*</span></Label>
                                                    <select className="form-select form-select-sm" {...register("labour.expense_category")}>
                                                        <option value="Maintenance">Maintenance</option>
                                                        <option value="Repair">Repair</option>
                                                        <option value="Inspection">Inspection</option>
                                                    </select>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Table bordered size="sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '75%' }}>Service Description</th>
                                                    <th style={{ width: '20%' }}>Rate</th>
                                                    <th style={{ width: '5%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {labourFieldArray.fields.map((field, index) => {
                                                    const itemError = (errors as any).labour?.items?.[index];
                                                    return (
                                                        <tr key={field.id}>
                                                            <td>
                                                                <input
                                                                    className={`form-control form-control-sm ${itemError?.item_description ? "is-invalid" : "border-0"}`}
                                                                    placeholder="Service Name"
                                                                    {...register(`labour.items.${index}.item_description` as const, { required: "Service description is required" })} autoComplete="off"
                                                                />
                                                                {itemError?.item_description && <span className="text-danger small">{itemError.item_description.message}</span>}
                                                            </td>
                                                            <input type="hidden" value="1" {...register(`labour.items.${index}.quantity` as const)} />
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control form-control-sm ${itemError?.unit_price ? "is-invalid" : "border-0"}`}
                                                                    step="0.01"
                                                                    {...register(`labour.items.${index}.unit_price` as const, { required: "Price is required", min: { value: 0.0001, message: "Price should be greater than 0" } })}
                                                                />
                                                                {itemError?.unit_price && <span className="text-danger small">{itemError.unit_price.message}</span>}
                                                            </td>
                                                            <td>
                                                                <Trash2
                                                                    size={14}
                                                                    className={`cursor-pointer ${labourFieldArray.fields.length > 1 ? "text-danger" : "text-muted"}`}
                                                                    onClick={() => labourFieldArray.fields.length > 1 && labourFieldArray.remove(index)}
                                                                    style={{ pointerEvents: labourFieldArray.fields.length > 1 ? 'auto' : 'none' }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        <Button color="warning" outline size="sm" className="w-100" onClick={() => labourFieldArray.append({ item_description: "", quantity: 1, unit_price: 0 })}>
                                            <Plus size={14} /> Add Service
                                        </Button>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    <div className="d-flex justify-content-between mt-3">
                        <Btn color="primary" size="lg" className="px-5" disabled={submitting}>
                            {submitting ? <> Creating<Spinner size="sm" /> </> : "Create"}
                        </Btn>
                        <div className="text-end me-4">
                            <small className="text-muted d-block">Grand Total</small>
                            <span className="fs-4 fw-bold text-primary">
                                {(partsTotal + labourTotal).toFixed(2)}
                            </span>
                        </div>

                    </div>
                </Form>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default CreateLPO;
